class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url.searchParams.set('options[unavailable_products]', 'show'),
      url.searchParams.set('options[fields]', 'title,product_type,variants.sku,tag,vendor'),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    if (this.isLikelySkuQuery(this.getQuery())) {
      this.handleSkuSearch();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  isLikelySkuQuery(query) {
    return /^[A-Za-z]{1,6}\d{3,}/i.test(query.trim());
  }

  async performSkuSearch(query) {
    const matches = [];
    const q = query.toLowerCase();
    let page = 1;
    while (page <= 20) {
      try {
        const res = await fetch(`/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const { products } = await res.json();
        if (!products || products.length === 0) break;
        for (const p of products) {
          if (p.variants && p.variants.some(v => v.sku && v.sku.toLowerCase() === q)) {
            matches.push(p);
          }
        }
        if (products.length < 250) break;
        page++;
      } catch (e) { break; }
    }
    return matches;
  }

  buildSkuResultsHTML(products, query) {
    if (products.length === 0) {
      return `<div class="empty-state flex flex-col gap-3 items-center justify-center text-center w-full h-full">
        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none"><path d="M39.375 45L41.25 50.625" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M30 45V52.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.625 45L18.75 50.625" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M41.4844 16.1297C50.2242 17.9555 56.25 21.7969 56.25 26.25C56.25 32.4633 44.4984 37.5 30 37.5C15.5016 37.5 3.75 32.4633 3.75 26.25C3.75 21.7969 9.81094 17.932 18.5977 16.1133" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.875 23.4141C16.8754 24.2579 17.1615 25.0767 17.6867 25.7372C18.2119 26.3977 18.9452 26.861 19.7672 27.0516C23.1271 27.7917 26.5596 28.1518 30 28.125C33.4388 28.1517 36.8698 27.7917 40.2281 27.0516C41.0498 26.8596 41.7828 26.3961 42.3084 25.736C42.8341 25.076 43.1218 24.2579 43.125 23.4141V22.5C43.1252 20.7617 42.78 19.0407 42.1096 17.4369C41.4392 15.8331 40.4569 14.3784 39.2197 13.1574C37.9825 11.9363 36.515 10.9732 34.9026 10.3239C33.2901 9.67456 31.5647 9.35203 29.8266 9.375C22.6125 9.46875 16.875 15.4828 16.875 22.6945V23.4141Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <h4>No results found for "${query}".</h4>
        <p class="m-0">Check the spelling or use a different word or phrase.</p>
      </div>`;
    }
    const items = products.slice(0, 5).map(p => {
      const imgSrc = p.images && p.images[0]
        ? p.images[0].src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_100x.$1')
        : '';
      const imgTag = imgSrc
        ? `<a href="/products/${p.handle}" class="media-wrapper blocks-radius-sm"><img src="${imgSrc}" alt="${p.title}" width="100" height="100" loading="lazy"></a>`
        : '';
      const price = p.variants && p.variants[0] ? `<span class="f-price-item f-price-item--regular">$${parseFloat(p.variants[0].price).toFixed(2)} AUD</span>` : '';
      return `<li class="predictive-search__list-item" role="option" aria-selected="false">
        <div class="flex items-center gap-3">
          ${imgTag}
          <div class="grid gap-1">
            <p class="block m-0"><a href="/products/${p.handle}" class="reversed-link text-pcard-title">${p.title}</a></p>
            ${price}
          </div>
        </div>
      </li>`;
    }).join('');
    return `<div class="grid gap-6 w-full">
      <div class="grid gap-3 predictive-search-result search__box-item predictive-search-result--products">
        <h4 class="h5 predictive-search-result__heading">Products</h4>
        <div class="grid"><ul class="grid gap-4" role="group">${items}</ul></div>
      </div>
      <div class="search__results-all block">
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="reversed-link btn--inherit flex items-center justify-center gap-2 font-body-bolder">
          See all results
        </a>
      </div>
    </div>`;
  }

  async handleSkuSearch() {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);
    if (!targetElement) return;
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');
    this.searchContent?.classList.remove('hidden');
    const query = this.getQuery();
    const products = await this.performSkuSearch(query);
    targetElement.innerHTML = this.buildSkuResultsHTML(products, query);
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
    this.setAttribute('results', 'true');
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
